// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SessionTimeoutModal from "@bg/App/Components/Common/SessionTimeoutModal";
import XngUtilities from "@bg/App/Components/Common/XngUtilities";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { IconWrapper } from "@bg/MobiusOverrides/Icon";
import { checkoutPages } from "@bg/XDL/constants";
import { screen } from "@bg/XDL/Helpers";
import { color } from "@bg/XDL/Utilities";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isSticky: state.xHeader.isSticky,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        // XNG-Changes
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        warehousePhone: getSession(state).pickUpWarehouse?.phone,
        isOrderConfirmationPage: getCurrentPage(state).type === "OnePageCheckoutConfirmationPage",
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    header?: ContainerProps;
    navigationWrapper?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    logoWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    globalMessageWrapper?: InjectableCss;
    secondaryNavigationPage?: ContainerProps;
    logoLinksSearchPage?: ContainerProps;
    globalMessagePage?: ContainerProps;
    // XNG-Changes
    checkoutHeader?: InjectableCss;
    branchPhone?: TypographyPresentationProps;
    checkoutHeaderPage?: ContainerProps;
    checkoutHeaderInnerContainer?: InjectableCss;
    branchPhoneLink?: LinkPresentationProps;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: 1286;
            &.order-confirmation-header {
                margin-bottom: 16px;
            }
        `,
    },
    globalMessageWrapper: {
        css: css`
            display: block;
            width: 100%;
            background-color: ${getColor("common.ui4")};
            ${({ isSticky }: any) =>
                isSticky &&
                `
                position: fixed;
                top: 90px;
                left: 0;
            `}
            ${screen("lte", "md")`
                position: fixed;
                top: 141px;
            `}
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            display: block;
            width: 100%;
            background-color: ${getColor("text.main")};
            ${({ isSticky }: any) =>
                isSticky &&
                `
            display: none;
            `}
            ${screen("lte", "md")`
                display:none;
            `}
        `,
    },
    navigationWrapper: {
        css: css`
            display: block;
            width: 100%;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    // XNG-Changes
    checkoutHeader: {
        css: css`
            box-shadow: 0 2px 0 0 ${color("common.ui3")};
            background: ${color("common.white")};

            ${({ isCMS }: any) =>
                !isCMS &&
                css`
                    position: fixed;
                    height: 64px;
                    width: 100%;
                    > main {
                        height: 100%;
                    }
                `}
        `,
    },
    checkoutHeaderInnerContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 32px;
            padding: 8px 0;
            height: 100%;
            > div:last-child {
                width: auto;
            }

            ${({ isCMS }: any) =>
                isCMS
                    ? css`
                          ${IconWrapper}, ${TypographyStyle} {
                              color: ${color("text.link")};
                          }
                      `
                    : css`
                          * {
                              max-height: 48px;
                          }
                      `}
        `,
    },
    branchPhone: {
        css: css`
            display: flex;
            gap: 4px;
            flex-wrap: nowrap;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    branchPhoneLink: {
        typographyProps: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    shellContext,
    // XNG-Changes
    isCheckoutPage,
    warehousePhone,
    isOrderConfirmationPage,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    /**
     * XNG-Changes
     * BG-2729
     *  Change structure, support different layout for checkout pages
     */
    return (
        <Page
            isHeader={true}
            {...styles.header}
            id="header"
            className={isOrderConfirmationPage ? "order-confirmation-header" : ""}
        >
            {(!isCheckoutPage || (shellContext.isInShell && shellContext.isEditing)) && (
                <>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <StyledWrapper {...styles.secondaryNavigationWrapper} {...({ isSticky } as any)}>
                        <Page {...styles.secondaryNavigationPage}>
                            <Zone contentId={id} zoneName="SecondaryNavigation" />
                        </Page>
                    </StyledWrapper>
                    <StyledWrapper {...styles.navigationWrapper} {...({ isSticky } as any)}>
                        <Zone contentId={id} zoneName="LogoLinksSearch" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.globalMessageWrapper} {...({ isSticky } as any)}>
                        <Page {...styles.globalMessagePage}>
                            <Zone contentId={id} zoneName="GlobalWebsiteMessage" />
                        </Page>
                    </StyledWrapper>
                </>
            )}
            {(isCheckoutPage || (shellContext.isInShell && shellContext.isEditing)) && (
                <StyledWrapper
                    {...styles.checkoutHeader}
                    {...{ isCMS: shellContext.isInShell && shellContext.isEditing }}
                >
                    <Page {...styles.checkoutHeaderPage}>
                        <StyledWrapper
                            {...styles.checkoutHeaderInnerContainer}
                            {...{ isCMS: shellContext.isInShell && shellContext.isEditing }}
                        >
                            {shellContext.isInShell && shellContext.isEditing && (
                                <Typography>Checkout Pages Header</Typography>
                            )}
                            <Zone contentId={id} zoneName="Logo" />
                            {warehousePhone && (
                                <StyledWrapper {...styles.branchPhone}>
                                    <Typography>{translate("Support:")}</Typography>
                                    <Link href={`tel:${warehousePhone}`} {...styles.branchPhoneLink}>
                                        {warehousePhone}
                                    </Link>
                                </StyledWrapper>
                            )}
                            <Zone contentId={id} zoneName="CartLink" />
                        </StyledWrapper>
                    </Page>
                </StyledWrapper>
            )}
            <span ref={afterHeader} tabIndex={-1} />
            {/** XNG-Changes */}
            <SessionTimeoutModal />
            <XngUtilities />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withToaster(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
